import React, { useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { compose, bindActionCreators } from 'redux';
import { connect, useSelector, shallowEqual } from 'react-redux';
import { withTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import { Grid, MenuItem, Menu } from '@mui/material';
import { AppBar, Tabs, Tab } from '@mui/material';
import OfferPartnerFormContainer from 'features/offer/edit/client/form/components/OfferPartnerFormContainer';
import OfferPartnerConsentContainer from 'features/offer/edit/client/consent/components/OfferPartnerConsentContainer';
import OfferPartnerVerificationContainer from 'features/offer/edit/client/verification/components/OfferPartnerVerificationContainer';
import OfferPartnerIncomeTypeContainer from 'features/offer/edit/client/income/components/OfferPartnerIncomeTypeContainer';
import OfferPartnerMonthlyObligationsContainer from 'features/offer/edit/client/monthlyObligations/components/OfferPartnerMonthlyObligationsContainer';
import OfferPartnerEligibilityContainer from 'features/offer/edit/client/eligibility/components/OfferPartnerEligibilityContainer';
import KnockoutCriteriaContainer from 'features/offer/edit/client/knockoutCriteria/components/KnockoutCriteriaContainer';
import OfferPartnerContactPersonsContainer from 'features/offer/edit/client/contactPersons/components/OfferPartnerContactPersonsContainer';
import OfferPartnerActivityFieldsContainer from 'features/offer/edit/client/activities/components/ActivityFieldsContainer';
import OfferPartnerShareholderContainer from 'features/offer/edit/client/shareholders/components/OfferPartnerShareholderContainer';
import OfferPartnerPropertyContainer from 'features/offer/edit/client/properties/components/OfferPartnerPropertyContainer';
import { loadPartnerIncomeTypes } from 'features/offer/edit/client/income/actionCreators';
import { deleteAssociatedPartner, deleteDocumentPartner, loadDocumentPartner, loadDocumentAssociatedPartners } from './form/actionCreators';
import { defaultPartnerId } from 'features/common/selectors';
import { withRouter } from 'react-router-dom';
import { RightsContext } from 'providers/RightsContext';
import { ApplicationFeaturesContext } from 'providers/ApplicationFeaturesContext';
import { getFullOfferHeader } from 'components/offer/edit/selectors';
import PersonWorkplacesContainer from './personWorkplaces/PersonWorkplacesContainer';
import OfferPartnerBankAccountContainer from './bankAccount/components/OfferPartnerBankAccountContainer';
import OfferPartnerListQuestionnaireContainer from './questionnaire/components/edit/OfferPartnerListQuestionnaireContainer';
import { dummyPartnerIds } from '../../../../constants/dummyPartnerIds';
import PartnerRelationsContainer from 'components/offer/edit/partnerRelations/PartnerRelationsContainer';
import { env } from 'utils/env';
import ReorderComponentsContainer from '../../../../components/common/reorderContainer/ReorderComponentsContainer';
import Explicit from '@mui/icons-material/Explicit';
import OfferPartnerContactModesContainer from './contactModes/components/OfferPartnerContactModesContainer';
import { CollapseCard } from '@totalsoft/rocket-ui';
import DocumentPartnerExtraInformationContainer from './extraInformation/DocumentPartnerExtraInformationContainer';

const defaultComponentsOrderForPF = {
  OfferPartnerFormContainer: 10,
  OfferPartnerIncomeTypeContainer: 20,
  OfferPartnerMonthlyObligationsContainer: 30,
  OfferPartnerEligibilityContainer: 40,
  OfferPartnerContactPersonsContainer: 50,
  OfferPartnerContactInfoContainer: 60,
  PersonWorkplacesContainer: 65,
  OfferPartnerBankAccountContainer: 70,
  OfferPartnerPropertyContainer: 80,
  OfferPartnerConsentContainer: 90,
  OfferPartnerListQuestionnaireContainer: 100,
  PartnerRelationsContainer: 110,
  OfferPartnerVerificationContainer: 120,
  KnockoutCriteriaContainer: 130,
  DocumentPartnerExtraInformationContainer: 140
};

const defaultComponentsOrderForPFA = {
  OfferPartnerFormContainer: 10,
  OfferPartnerIncomeTypeContainer: 20,
  OfferPartnerContactPersonsContainer: 30,
  OfferPartnerContactInfoContainer: 40,
  OfferPartnerBankAccountContainer: 50,
  OfferPartnerShareholderContainer: 60,
  OfferPartnerActivityFieldsContainer: 70,
  OfferPartnerPropertyContainer: 80,
  OfferPartnerConsentContainer: 90,
  OfferPartnerEligibilityContainer: 100,
  OfferPartnerListQuestionnaireContainer: 110,
  OfferPartnerVerificationContainer: 120,
  KnockoutCriteriaContainer: 130,
  DocumentPartnerExtraInformationContainer: 140
};

const defaultComponentsOrderForPJ = {
  OfferPartnerFormContainer: 10,
  OfferPartnerContactPersonsContainer: 20,
  OfferPartnerContactInfoContainer: 30,
  OfferPartnerBankAccountContainer: 40,
  OfferPartnerShareholderContainer: 50,
  OfferPartnerActivityFieldsContainer: 60,
  OfferPartnerPropertyContainer: 70,
  OfferPartnerConsentContainer: 80,
  OfferPartnerEligibilityContainer: 90,
  OfferPartnerListQuestionnaireContainer: 100,
  OfferPartnerVerificationContainer: 110,
  KnockoutCriteriaContainer: 120,
  DocumentPartnerExtraInformationContainer: 140
};

export const OfferClientContainer = ({
  partnerId,
  actions,
  offerHeader,
  documentId,
  siteId,
  t,
  history,
  onChangeActiveStep,
  isFacilityFrame,
  partnerSaving,
  documentPartnerAllowedRoles,
  documentPartnerExtraInfoParam
}) => {
  const [value, setValue] = useState(0);
  const [openQuestionnaire, setOpenQuestionnaire] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPartnerName, setSelectedPartnerName] = useState('');
  const [allowedRolesAssociatedPartners, setAllowedRolesAssociatedPartners] = useState([]);
  const [associatedPartners, setAssociatedPartners] = useState([]);
  const containerRef = useRef();

  const documentPartnerAllowedRolesValues = documentPartnerAllowedRoles?.split(';');
  const associatedPartnersKey = `${documentId}-${siteId}`;

  const associatedPartnersLoaded = useSelector(
    state =>
      state.currentOffer.associatedPartners[associatedPartnersKey]
        ? state.currentOffer.associatedPartners[associatedPartnersKey]?.loaded
        : false,
    shallowEqual
  );

  const associatedPartnerList = useSelector(
    state => (state.entities.associatedPartners[associatedPartnersKey] ? state.entities.associatedPartners[associatedPartnersKey] : []),
    shallowEqual
  );

  const filterAssociatedPartners = () => {
    return associatedPartnerList.filter(partner => !documentPartnerAllowedRolesValues.includes(partner.partnerRoleCode));
  };

  const filterAllowedRolesAssociatedPartners = () => {
    return associatedPartnerList.filter(partner => documentPartnerAllowedRolesValues.includes(partner.partnerRoleCode));
  };

  const {
    clientIncomeSourcesRead,
    clientMonthlyObligationsRead,
    clientVerificationsRead,
    readOnlyFull,
    partnerQuestionnaireRead,
    clientShareholdersRead,
    clientBankaccountRead,
    clientPersonworkplacesRead,
    clientContactinfoRead,
    clientContactmodeRead,
    clientActivityfieldsRead,
    clientShareholdersWrite,
    clientBankaccountWrite,
    clientPersonworkplacesWrite,
    clientContactinfoWrite,
    clientEligibilityRead,
    clientConsentsRead,
    clientRelationsRead,
    clientPropertiesRead,
    clientKnockoutCriteriaRead,
    clientKnockoutCriteriaWrite,
    masterPartnerApp,
    masterPartnerRead,
    clientExtraInformationRead,
    clientExtraInformationWrite
  } = useContext(RightsContext);

  const { enablePartnerRelations } = useContext(ApplicationFeaturesContext);

  const onPrintClick = () => {
    onChangeActiveStep(-1);
    history.push(`/offers/${documentId}/${siteId}/documents`);
  };

  const loadAssociatedPartnerList = async (documentId, siteId) => {
    await actions.loadDocumentAssociatedPartners(documentId, siteId);
  };

  useEffect(() => {
    window.addEventListener('message', event => {
      if (event.origin === window.location.origin && event.data === true) {
        const openQuest = event.data;
        setOpenQuestionnaire(openQuest);

        const container = containerRef.current;
        if (container !== null && container !== undefined) {
          setTimeout(() => {
            container.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
          }, 500);
        }
      }
    });
  }, []);

  const masterPartnerUrl = env.REACT_APP_LSNG_MASTERPARTNER_URL;

  useEffect(() => {
    if (
      partnerId !== undefined &&
      partnerId !== 0 &&
      !isDefaultPartnerBehaviour() &&
      !isRealBeneficiaryPartnerBehaviour() &&
      !isAssociatedDocPartner()
    ) {
      const index = allowedRolesAssociatedPartners.findIndex(a => a.partnerId === partnerId);
      changeIndex(index + 1);
      loadAssociatedPartnerInternal(partnerId);
    } else if (isDefaultPartnerBehaviour() || isRealBeneficiaryPartnerBehaviour()) {
      changeIndex(allowedRolesAssociatedPartners.length + 1);
    } else if (isAssociatedDocPartner()) {
      changeIndex(allowedRolesAssociatedPartners.length + 2);
      changeSelectedPartnerName();
    } else {
      changeIndex(0);
    }
  }, [partnerId, allowedRolesAssociatedPartners, associatedPartners]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      (isDefaultPartnerBehaviour() || isRealBeneficiaryPartnerBehaviour()) &&
      offerHeader.lastChangedAssociatedPartnerId !== undefined &&
      !partnerSaving
    ) {
      history.push(`/offers/${documentId}/${siteId}/partner/${offerHeader.lastChangedAssociatedPartnerId}`);
    }
  }, [offerHeader.lastChangedAssociatedPartnerId, partnerSaving]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (associatedPartnersLoaded === false) loadAssociatedPartnerList(documentId, siteId);
    else {
      setAssociatedPartners(filterAssociatedPartners());
      setAllowedRolesAssociatedPartners(filterAllowedRolesAssociatedPartners());
      changeSelectedPartnerName();
    }
  }, [associatedPartnersLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadAssociatedPartnerList(documentId, siteId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAssociatedPartnerInternal = async partnerId => {
    await loadAssociatedPartner(partnerId);
  };

  const changeIndex = index => {
    setValue(index);
  };

  const openMasterPartner = masterPartnerId => e => {
    e.stopPropagation();
    if (!masterPartnerApp || !masterPartnerRead) return;
    window.open(`${masterPartnerUrl}/partners/${masterPartnerId}`, '_blank');
  };

  const openDocumentPartner = (partnerId, partnerName, partnerRole) => e => {
    e.stopPropagation();
    openAssociatedPartner(partnerId, partnerName, partnerRole);
  };

  const loadAssociatedPartner = async partnerId => {
    if (!partnerId) return;

    if (clientIncomeSourcesRead) await actions.loadPartnerIncomeTypes(documentId, siteId, partnerId);
    return await actions.loadDocumentPartner(documentId, siteId, partnerId, offerHeader.documentTypeId);
  };

  const handleChange = async (_, value) => {
    if ((readOnlyFull && value === allowedRolesAssociatedPartners.length + 1) || value === allowedRolesAssociatedPartners.length + 2)
      return;
    setSelectedPartnerName('');
    if (value === 0) {
      history.push(`/offers/${documentId}/${siteId}/client`);
      return;
    }
    let partner = allowedRolesAssociatedPartners[value - 1];
    if (partner?.partnerId) {
      history.push(`/offers/${documentId}/${siteId}/partner/${partner.partnerId}`);
    } else {
      history.push(`/offers/${documentId}/${siteId}/partner/${-1}`);
    }
  };

  const handlePartnerClick = partner => {
    openAssociatedPartner(partner.partnerId, partner.name, partner.partnerRole);
  };

  const handleSelectPartner = event => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openAssociatedPartner = (partnerId, partnerName, partnerRole) => {
    setAnchorEl(null);
    setSelectedPartnerName(partnerRole + ': ' + partnerName);
    changeIndex(allowedRolesAssociatedPartners.length + 2);
    history.push(`/offers/${documentId}/${siteId}/partner/${partnerId}`);
  };

  const changeSelectedPartnerName = () => {
    const partner = associatedPartners.filter(partner => partner.partnerId === partnerId)[0];
    if (partner) setSelectedPartnerName(partner.partnerRole + ': ' + partner.name);
  };

  const handleRemovePartner = async data => {
    await actions.deleteAssociatedPartner(data);
    setValue(0);
    setSelectedPartnerName('');
    history.push(`/offers/${documentId}/${siteId}/client`);
  };

  const handleRemoveDocumentPartner = async data => {
    await actions.deleteDocumentPartner(data);
    setValue(0);
    setSelectedPartnerName('');
    history.push(`/offers/${documentId}/${siteId}/client`);
  };

  const isDefaultPartnerBehaviour = () => {
    return partnerId === dummyPartnerIds.DefaultAssociatedPartner;
  };

  const isRealBeneficiaryPartnerBehaviour = () => {
    return partnerId === dummyPartnerIds.RealBeneficiaryAssociatedPartner;
  };

  const isAssociatedDocPartner = () => {
    return associatedPartners.filter(partner => partner.partnerId === partnerId).length > 0;
  };

  const isCardVisible = (partner, id) => {
    const visibilitiesByRole = offerHeader.clientCardVisibilitiesByRole?.find(x => x.partnerRoleCode === partner.partnerRoleCode);
    if (!visibilitiesByRole?.cards || !Object.prototype.hasOwnProperty.call(visibilitiesByRole.cards, id)) return true;
    return visibilitiesByRole.cards[id];
  };

  const client = offerHeader.client;

  const getComponent = (componentName, reorderEnabled, partner) => {
    switch (componentName) {
      case 'OfferPartnerFormContainer':
        return (
          <OfferPartnerFormContainer
            documentId={documentId}
            siteId={siteId}
            partnerId={partner.clientId || 0}
            onDelete={() => {}}
            onDeleteDocumentPartner={handleRemoveDocumentPartner}
            documentTypeId={offerHeader.documentTypeId}
            isFacilityFrame={isFacilityFrame}
          />
        );

      case 'OfferPartnerIncomeTypeContainer':
        return (
          (reorderEnabled || (partner.clientId && clientIncomeSourcesRead && !isFacilityFrame)) && (
            <OfferPartnerIncomeTypeContainer
              id={'offerClient_IncomeTypeCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.clientId}
            />
          )
        );

      case 'OfferPartnerMonthlyObligationsContainer':
        return (
          (reorderEnabled || (partner.clientId && clientMonthlyObligationsRead && !isFacilityFrame)) && (
            <OfferPartnerMonthlyObligationsContainer
              id={'offerClient_MonthlyObligationsCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.clientId}
            />
          )
        );

      case 'OfferPartnerEligibilityContainer':
        return reorderEnabled ? (
          <CollapseCard icon={Explicit} title={t('OfferEdit.Client.Eligibility.Title')} toggleOnHeaderClick />
        ) : (
          clientEligibilityRead && !isFacilityFrame && (
            <OfferPartnerEligibilityContainer
              id={'offerClient_EligibilityCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.clientId}
            />
          )
        );

      case 'OfferPartnerContactPersonsContainer':
        return (
          (reorderEnabled || clientContactinfoRead) && (
            <OfferPartnerContactPersonsContainer
              id={'offerClient_ContactPersonsCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.clientId}
              isAssociatedPartner={false}
              openMasterPartner={openMasterPartner}
              openDocumentPartner={openDocumentPartner}
              masterPartnerApp={masterPartnerApp && masterPartnerRead}
              clientContactinfoWrite={clientContactinfoWrite}
            />
          )
        );

      case 'OfferPartnerContactInfoContainer':
        return (
          (reorderEnabled || clientContactmodeRead) && (
            <OfferPartnerContactModesContainer id={'offerClient_ContactInfoCard'} partnerId={partner.clientId} />
          )
        );

      case 'PersonWorkplacesContainer':
        return (
          (reorderEnabled || clientPersonworkplacesRead) && (
            <PersonWorkplacesContainer partnerId={partner.clientId} documentId={documentId} siteId={siteId} clientPersonworkplacesWrite={clientPersonworkplacesWrite}/>
          )
        );

      case 'OfferPartnerBankAccountContainer':
        return (
          (reorderEnabled || (partner.clientId && clientBankaccountRead)) && (
            <OfferPartnerBankAccountContainer
              id={'offerClient_BankAccountCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.clientId}
              clientBankaccountWrite={clientBankaccountWrite}
            />
          )
        );

      case 'OfferPartnerPropertyContainer':
        return (
          (reorderEnabled || (partner.clientId && clientPropertiesRead)) && (
            <OfferPartnerPropertyContainer
              id={'offerClient_PropertyCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.clientId}
            />
          )
        );

      case 'DocumentPartnerExtraInformationContainer':
        return (
          (reorderEnabled || (partner.clientId && clientExtraInformationRead && documentPartnerExtraInfoParam)) && (
            <DocumentPartnerExtraInformationContainer
              id={'offerClient_ExtraInfoCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.clientId}
              clientExtraInformationWrite={clientExtraInformationWrite}
            />
          )
        );

      case 'OfferPartnerConsentContainer':
        return (
          (reorderEnabled || (partner.clientId && !isFacilityFrame && clientConsentsRead)) && (
            <OfferPartnerConsentContainer
              id={'offerClient_ConsentCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.clientId}
              onPrintClick={onPrintClick}
            />
          )
        );

      case 'OfferPartnerListQuestionnaireContainer':
        return (
          (reorderEnabled || (partner.clientId && partnerQuestionnaireRead && !isFacilityFrame)) && (
            <div ref={containerRef}>
              <OfferPartnerListQuestionnaireContainer
                id={'offerClient_QuestionnaireCard'}
                documentId={documentId}
                siteId={siteId}
                openQuestionnaire={openQuestionnaire}
                partnerId={partner.clientId}
                history={history}
                onChangeActiveStep={onChangeActiveStep}
              />
            </div>
          )
        );

      case 'PartnerRelationsContainer':
        return (
          (reorderEnabled || (!isFacilityFrame && clientRelationsRead && enablePartnerRelations)) && (
            <PartnerRelationsContainer
              id={'offerClient_RelationsCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.clientId}
              startsExpanded={false}
            />
          )
        );

      case 'OfferPartnerShareholderContainer':
        return (
          (reorderEnabled || (partner.clientId && clientShareholdersRead)) && (
            <OfferPartnerShareholderContainer
              id={'offerClient_ShareholderCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.clientId}
              isAssociatedPartner={false}
              openMasterPartner={openMasterPartner}
              openDocumentPartner={openDocumentPartner}
              clientShareholdersWrite={clientShareholdersWrite}
              masterPartnerApp={masterPartnerApp && masterPartnerRead}
            />
          )
        );

      case 'OfferPartnerActivityFieldsContainer':
        return (
          (reorderEnabled || (partner.clientId && clientActivityfieldsRead)) && (
            <OfferPartnerActivityFieldsContainer
              id={'offerClient_ActivityFieldsCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.clientId}
            />
          )
        );

      case 'OfferPartnerVerificationContainer':
        return (
          (reorderEnabled || (partner.clientId && clientVerificationsRead)) && (
            <OfferPartnerVerificationContainer
              id={'offerClient_VerificationCard'}
              offerHeader={offerHeader}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.clientId}
            />
          )
        );

      case 'KnockoutCriteriaContainer':
        return (
          (reorderEnabled || (partner.clientId && clientKnockoutCriteriaRead)) && (
            <KnockoutCriteriaContainer
              id={'offerClient_KnockoutCriteriaCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.clientId}
              disabled={!clientKnockoutCriteriaWrite}
            />
          )
        );

      default:
        return null;
    }
  };

  const getReorderJsonName = partner => {
    if (partner.isPF) return 'offer_client_forPF';
    else if (partner.isPFA) return 'offer_client_forPFA';
    else return 'offer_client_forPJ';
  };

  const getDefaultComponentsOrder = partner => {
    if (partner.isPF) return defaultComponentsOrderForPF;
    else if (partner.isPFA) return defaultComponentsOrderForPFA;
    else return defaultComponentsOrderForPJ;
  };

  const getAssociatedPartnerComponent = (componentName, reorderEnabled, p) => {
    switch (componentName) {
      case 'OfferPartnerFormContainer':
        return (
          <OfferPartnerFormContainer
            documentId={documentId}
            siteId={siteId}
            partnerId={p.partnerId}
            isAssociatedPartner={true}
            isRealBeneficiary={partnerId === dummyPartnerIds.RealBeneficiaryAssociatedPartner}
            onDelete={handleRemovePartner}
            onDeleteDocumentPartner={handleRemoveDocumentPartner}
            documentTypeId={offerHeader.documentTypeId}
            isFacilityFrame={isFacilityFrame}
          />
        );

      case 'OfferPartnerIncomeTypeContainer':
        return (
          (reorderEnabled || (clientIncomeSourcesRead && !isFacilityFrame && isCardVisible(p, 'offerClient_IncomeTypeCard'))) && (
            <OfferPartnerIncomeTypeContainer
              id={'offerClient_IncomeTypeCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={p.partnerId}
              isAssociatedPartner={true}
            />
          )
        );

      case 'OfferPartnerMonthlyObligationsContainer':
        return (
          (reorderEnabled ||
            (clientMonthlyObligationsRead && !isFacilityFrame && isCardVisible(p, 'offerClient_MonthlyObligationsCard'))) && (
            <OfferPartnerMonthlyObligationsContainer
              id={'offerClient_MonthlyObligationsCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={p.partnerId}
              isAssociatedPartner={true}
            />
          )
        );

      case 'OfferPartnerEligibilityContainer':
        return reorderEnabled ? (
          <CollapseCard icon={Explicit} title={t('OfferEdit.Client.Eligibility.Title')} toggleOnHeaderClick />
        ) : (
          clientEligibilityRead && isCardVisible(p, 'offerClient_EligibilityCard') && (
            <OfferPartnerEligibilityContainer
              id={'offerClient_EligibilityCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={p.partnerId}
            />
          )
        );

      case 'OfferPartnerContactPersonsContainer':
        return (
          (reorderEnabled || clientContactinfoRead) && (
            <OfferPartnerContactPersonsContainer
              id={'offerClient_ContactPersonsCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={p.partnerId}
              isAssociatedPartner={true}
              openDocumentPartner={openDocumentPartner}
              openMasterPartner={openMasterPartner}
              masterPartnerApp={masterPartnerApp && masterPartnerRead}
              clientContactinfoWrite={clientContactinfoWrite}
            />
          )
        );

      case 'OfferPartnerContactInfoContainer':
        return (
          (reorderEnabled || clientContactmodeRead) && (
            <OfferPartnerContactModesContainer id={'offerClient_ContactInfoCard'} partnerId={p.partnerId} isAssociatedPartner={true} />
          )
        );

      case 'OfferPartnerBankAccountContainer':
        return (
          (reorderEnabled || clientBankaccountRead) && (
            <OfferPartnerBankAccountContainer
              id={'offerClient_BankAccountCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={p.partnerId}
              clientBankaccountWrite={clientBankaccountWrite}
            />
          )
        );

      case 'OfferPartnerPropertyContainer':
        return (
          (reorderEnabled || clientPropertiesRead) && (
            <OfferPartnerPropertyContainer
              id={'offerClient_PropertyCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={p.partnerId}
            />
          )
        );

      case 'OfferPartnerConsentContainer':
        return (
          (reorderEnabled || isCardVisible(p, 'offerClient_ConsentCard')) && (
            <OfferPartnerConsentContainer
              id={'offerClient_ConsentCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={p.partnerId}
              isAssociatedPartner={true}
              onPrintClick={onPrintClick}
            />
          )
        );

      case 'DocumentPartnerExtraInformationContainer':
        return (
          (reorderEnabled ||
            (clientExtraInformationRead && documentPartnerExtraInfoParam && isCardVisible(p, 'offerClient_ExtraInfoCard'))) && (
            <DocumentPartnerExtraInformationContainer
              id={'offerClient_ExtraInfoCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={p.partnerId}
              clientExtraInformationWrite={clientExtraInformationWrite}
            />
          )
        );

      case 'OfferPartnerListQuestionnaireContainer':
        return (
          (reorderEnabled || (partnerQuestionnaireRead && isCardVisible(p, 'offerClient_QuestionnaireCard'))) && (
            <div ref={containerRef}>
              <OfferPartnerListQuestionnaireContainer
                id={'offerClient_QuestionnaireCard'}
                documentId={documentId}
                siteId={siteId}
                openQuestionnaire={openQuestionnaire}
                partnerId={p.partnerId}
                history={history}
                onChangeActiveStep={onChangeActiveStep}
              />
            </div>
          )
        );

      case 'PartnerRelationsContainer':
        return (
          (reorderEnabled ||
            (clientRelationsRead && isCardVisible(p, 'offerClient_RelationsCard') && !isFacilityFrame && enablePartnerRelations)) && (
            <PartnerRelationsContainer
              id={'offerClient_RelationsCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={p.partnerId}
              startsExpanded={false}
            />
          )
        );

      case 'OfferPartnerShareholderContainer':
        return (
          (reorderEnabled || clientShareholdersRead) && (
            <OfferPartnerShareholderContainer
              id={'offerClient_ShareholderCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={p.partnerId}
              openMasterPartner={openMasterPartner}
              openDocumentPartner={openDocumentPartner}
              isAssociatedPartner={true}
              clientShareholdersWrite={clientShareholdersWrite}
              masterPartnerApp={masterPartnerApp && masterPartnerRead}
            />
          )
        );

      case 'OfferPartnerActivityFieldsContainer':
        return (
          (reorderEnabled || clientActivityfieldsRead) && (
            <OfferPartnerActivityFieldsContainer
              id={'offerClient_ActivityFieldsCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={p.partnerId}
            />
          )
        );

      case 'OfferPartnerVerificationContainer':
        return (
          (reorderEnabled || (clientVerificationsRead && isCardVisible(p, 'offerClient_VerificationCard'))) && (
            <OfferPartnerVerificationContainer
              id={'offerClient_VerificationCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={p.partnerId}
            />
          )
        );

      case 'KnockoutCriteriaContainer':
        return (
          (reorderEnabled || (p.partnerId && clientKnockoutCriteriaRead)) && (
            <KnockoutCriteriaContainer
              id={'offerClient_KnockoutCriteriaCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={p.partnerId}
            />
          )
        );

      default:
        return null;
    }
  };

  return (
    <>
      <AppBar position='static' color='default'>
        <Grid container justifyContent='space-between'>
          <Grid item>
            <Tabs
              value={value}
              onChange={handleChange}
              indicatorColor='primary'
              textColor='primary'
              variant='scrollable'
              position='absolute'
            >
              {client && (
                <Tab
                  id={'offerClient_client'}
                  label={client.clientName ? t('OfferEdit.Client.Title') + ': ' + client.clientName : t('OfferEdit.Client.Title')}
                />
              )}
              {allowedRolesAssociatedPartners &&
                allowedRolesAssociatedPartners.map((partner, index) => {
                  return (
                    <Tab id={'offerClient_associatedPartner_' + index} key={index} label={partner.partnerRole + ': ' + partner.name} />
                  );
                })}
              {!readOnlyFull && (
                <Tab
                  id={'offerClient_addPartnerBtn'}
                  disabled={partnerSaving}
                  value={-1}
                  sx={{ p: 0 }}
                  icon={<AddIcon color='primary' />}
                />
              )}
            </Tabs>
          </Grid>
          <Grid item>
            <Tabs value={0} onChange={handleChange}>
              {associatedPartnersLoaded && (
                <Tab
                  id={'offerClient_associatedPartners'}
                  onClick={handleSelectPartner}
                  label={selectedPartnerName === '' ? t('OfferEdit.Client.AssociatedPartner') : selectedPartnerName}
                  wrapped
                />
              )}
              {anchorEl !== null && associatedPartnersLoaded && associatedPartners.length > 0 && (
                <Menu id='decision-type-menu' anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleMenuClose}>
                  {associatedPartners.map((part, index) => {
                    return (
                      <MenuItem key={'offerClient_associatedPartners_' + index} onClick={() => handlePartnerClick(part)}>
                        {part.partnerRole + ': ' + part.name}
                      </MenuItem>
                    );
                  })}
                </Menu>
              )}
            </Tabs>
          </Grid>
        </Grid>
      </AppBar>
      <>
        {value === 0 && client && (
          <ReorderComponentsContainer
            reorderComponentsName={getReorderJsonName(client)}
            defaultComponentsOrder={getDefaultComponentsOrder(client)}
            getComponent={getComponent}
            getComponentExtraParams={client}
          />
        )}
        {allowedRolesAssociatedPartners &&
          allowedRolesAssociatedPartners.map((p, index) => {
            return (
              index + 1 === value && (
                <ReorderComponentsContainer
                  reorderComponentsName={getReorderJsonName(p)}
                  defaultComponentsOrder={getDefaultComponentsOrder(p)}
                  getComponent={getAssociatedPartnerComponent}
                  getComponentExtraParams={p}
                />
              )
            );
          })}
        {value === allowedRolesAssociatedPartners.length + 2 &&
          associatedPartnersLoaded &&
          associatedPartners &&
          associatedPartners.map((p, _) => {
            return (
              p.partnerId === partnerId && (
                <ReorderComponentsContainer
                  reorderComponentsName={getReorderJsonName(p)}
                  defaultComponentsOrder={getDefaultComponentsOrder(p)}
                  getComponent={getAssociatedPartnerComponent}
                  getComponentExtraParams={p}
                />
              )
            );
          })}
        {value === allowedRolesAssociatedPartners.length + 1 && (
          <div>
            <OfferPartnerFormContainer
              documentId={documentId}
              siteId={siteId}
              partnerId={defaultPartnerId}
              isAssociatedPartner={true}
              isRealBeneficiary={partnerId === dummyPartnerIds.RealBeneficiaryAssociatedPartner}
              onDelete={handleRemovePartner}
              onDeleteDocumentPartner={handleRemoveDocumentPartner}
              documentTypeId={offerHeader.documentTypeId}
              isFacilityFrame={isFacilityFrame}
            />
          </div>
        )}
      </>
    </>
  );
};

OfferClientContainer.propTypes = {
  actions: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  offerHeader: PropTypes.object.isRequired,
  partnerId: PropTypes.number,
  history: PropTypes.object.isRequired,
  onChangeActiveStep: PropTypes.func.isRequired,
  isFacilityFrame: PropTypes.bool,
  partnerSaving: PropTypes.bool,
  documentPartnerAllowedRoles: PropTypes.string.isRequired,
  documentPartnerExtraInfoParam: PropTypes.string.isRequired
};

function mapStateToProps(state) {
  const { saving } = state.currentOffer.clientData;
  return {
    partnerSaving: saving,
    offerHeader: getFullOfferHeader(state.currentOffer.header, state.entities.offerPartners),
    documentPartnerAllowedRoles: state.params.items.documentPartnerAllowedRoles,
    documentPartnerExtraInfoParam: state.params.items.documentPartnerExtraInfo
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadDocumentPartner,
        loadPartnerIncomeTypes,
        deleteAssociatedPartner,
        deleteDocumentPartner,
        loadDocumentAssociatedPartners
      },
      dispatch
    ),
    dispatch
  };
}

export default compose(withRouter, connect(mapStateToProps, mapDispatchToProps), withTranslation())(OfferClientContainer);
